import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { AddressBook, Buildings, Users, Truck, UserCircle, Plus, PencilSimple, Trash, X, Phone, Envelope, MapPin, MagnifyingGlass } from '@phosphor-icons/react';
import { SearchBar } from '../components/SearchBar';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { LayoutToggle, useLayoutMode, getLayoutContainerProps } from '../components/ui/LayoutToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'entreprise', label: 'Entreprise', icon: Buildings, color: '#1565C0' },
  { key: 'collaborateur', label: 'Collaborateur', icon: Users, color: '#6A1B9A' },
  { key: 'fournisseur', label: 'Fournisseur', icon: Truck, color: '#EF6C00' },
  { key: 'client', label: 'Client', icon: UserCircle, color: '#2E7D32' },
];

export const EMPTY_FORM = { category: 'entreprise', name: '', company: '', job_title: '', phone: '', phone2: '', email: '', address: '', city: '', postcode: '', notes: '' };

const ContactCard = ({ contact, isAdmin, onEdit, onDelete, catColor }) => (
  <div className="card" data-testid={`contact-${contact.id}`} style={{ padding: '16px', transition: 'box-shadow 0.2s' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: `${catColor}15`, color: catColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1rem', flexShrink: 0 }}>
          {contact.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>{contact.name}</div>
          {contact.company && <div style={{ fontSize: '0.8rem', color: '#5E5F66' }}>{contact.company}</div>}
          {contact.job_title && <div style={{ fontSize: '0.75rem', color: '#9E9E9E' }}>{contact.job_title}</div>}
        </div>
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => onEdit(contact)} data-testid={`edit-contact-${contact.id}`}><PencilSimple size={14} /></button>
          <button className="btn btn-danger btn-sm btn-icon" onClick={() => onDelete(contact.id)} data-testid={`delete-contact-${contact.id}`}><Trash size={14} /></button>
        </div>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', fontSize: '0.82rem' }}>
      {contact.phone && (
        <a href={`tel:${contact.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1565C0', textDecoration: 'none' }} data-testid={`phone-${contact.id}`}>
          <Phone size={13} /> {contact.phone}
        </a>
      )}
      {contact.phone2 && (
        <a href={`tel:${contact.phone2}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1565C0', textDecoration: 'none' }}>
          <Phone size={13} /> {contact.phone2}
        </a>
      )}
      {contact.email && (
        <a href={`mailto:${contact.email}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1565C0', textDecoration: 'none' }} data-testid={`email-${contact.id}`}>
          <Envelope size={13} /> {contact.email}
        </a>
      )}
      {(contact.address || contact.city) && (
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#5E5F66' }}>
          <MapPin size={13} /> {[contact.address, contact.postcode, contact.city].filter(Boolean).join(', ')}
        </span>
      )}
    </div>
    {contact.notes && <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: '#FAFAFA', borderRadius: '4px', fontSize: '0.8rem', color: '#5E5F66' }}>{contact.notes}</div>}
  </div>
);

export const ContactFormModal = ({ show, onClose, onSubmit, form, setForm, editMode, lockedCategory = null }) => {
  // Force category if locked (must be called before any early return — Rules of Hooks)
  React.useEffect(() => {
    if (show && lockedCategory && form.category !== lockedCategory) {
      setForm(prev => ({ ...prev, category: lockedCategory }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCategory, show]);
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <h3 style={{ margin: 0 }}>{editMode ? 'Modifier le contact' : (lockedCategory === 'client' ? 'Nouveau client' : 'Nouveau contact')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={onSubmit} style={{ padding: '20px' }}>
          {!lockedCategory && (
            <div className="form-group">
              <label className="form-label">Categorie *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {CATEGORIES.map(cat => (
                  <button key={cat.key} type="button" data-testid={`cat-${cat.key}`}
                    onClick={() => setForm({ ...form, category: cat.key })}
                    style={{
                      padding: '10px 4px', border: form.category === cat.key ? `2px solid ${cat.color}` : '2px solid #E2E2E6',
                      borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                      backgroundColor: form.category === cat.key ? `${cat.color}10` : 'white', fontSize: '0.75rem', fontWeight: form.category === cat.key ? '700' : '400', color: form.category === cat.key ? cat.color : '#5E5F66'
                    }}>
                    <cat.icon size={18} weight={form.category === cat.key ? 'fill' : 'regular'} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input type="text" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Nom complet ou raison sociale" data-testid="contact-name-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Societe</label>
              <input type="text" className="form-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Nom de la societe" />
            </div>
            <div className="form-group">
              <label className="form-label">Fonction</label>
              <input type="text" className="form-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Ex: Gerant, Comptable" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Telephone</label>
              <input type="tel" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="contact-phone-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Telephone 2</label>
              <input type="tel" className="form-input" value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} placeholder="Fixe, bureau..." />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemple.fr" data-testid="contact-email-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Adresse</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(val, geo) => {
                const updates = { ...form, address: val };
                if (geo) { updates.city = geo.city || ''; updates.postcode = geo.postcode || ''; }
                setForm(updates);
              }}
              placeholder="Rechercher une adresse..."
              data-testid="contact-address-input"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Code postal</label>
              <input type="text" className="form-input" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Ville</label>
              <input type="text" className="form-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Informations complementaires..." />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-contact-btn">{editMode ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ContactsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [layoutMode, setLayoutMode] = useLayoutMode('contacts', 'grid');
  const [showModal, setShowModal] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contacts`, { withCredentials: true });
      setContacts(res.data);
    } catch (e) { toast.error('Erreur chargement contacts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editContact) {
        await axios.put(`${API_URL}/api/contacts/${editContact.id}`, form, { withCredentials: true });
        toast.success('Contact mis a jour');
      } else {
        await axios.post(`${API_URL}/api/contacts`, form, { withCredentials: true });
        toast.success('Contact ajoute');
      }
      setShowModal(false); setEditContact(null); setForm({ ...EMPTY_FORM });
      fetchContacts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (contact) => {
    setEditContact(contact);
    setForm({
      category: contact.category, name: contact.name, company: contact.company || '',
      job_title: contact.job_title || '', phone: contact.phone || '', phone2: contact.phone2 || '',
      email: contact.email || '', address: contact.address || '', city: contact.city || '',
      postcode: contact.postcode || '', notes: contact.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try { await axios.delete(`${API_URL}/api/contacts/${id}`, { withCredentials: true }); toast.success('Contact supprime'); fetchContacts(); }
    catch (e) { toast.error('Erreur suppression'); }
  };

  const openAdd = (category = 'entreprise') => {
    setEditContact(null);
    setForm({ ...EMPTY_FORM, category });
    setShowModal(true);
  };

  const filtered = contacts.filter(c => {
    if (activeCategory !== 'all' && c.category !== activeCategory) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return [c.name, c.company, c.email, c.phone, c.city, c.notes].some(v => (v || '').toLowerCase().includes(s));
    }
    return true;
  });

  const catCounts = CATEGORIES.reduce((acc, cat) => { acc[cat.key] = contacts.filter(c => c.category === cat.key).length; return acc; }, {});
  const getCatColor = (cat) => CATEGORIES.find(c => c.key === cat)?.color || '#5E5F66';

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="contacts-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AddressBook size={28} weight="bold" /> Contacts
          </h1>
          <p className="page-subtitle">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => openAdd(activeCategory !== 'all' ? activeCategory : 'entreprise')} data-testid="add-contact-btn">
            <Plus size={16} /> Ajouter un contact
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => setActiveCategory('all')} data-testid="filter-all"
          style={{ padding: '8px 16px', border: activeCategory === 'all' ? '2px solid #0A0B10' : '2px solid #E2E2E6', borderRadius: '20px', cursor: 'pointer', backgroundColor: activeCategory === 'all' ? '#0A0B10' : 'white', color: activeCategory === 'all' ? 'white' : '#5E5F66', fontWeight: '600', fontSize: '0.82rem' }}>
          Tous ({contacts.length})
        </button>
        {CATEGORIES.map(cat => (
          <button key={cat.key} onClick={() => setActiveCategory(cat.key)} data-testid={`filter-${cat.key}`}
            style={{ padding: '8px 16px', border: activeCategory === cat.key ? `2px solid ${cat.color}` : '2px solid #E2E2E6', borderRadius: '20px', cursor: 'pointer', backgroundColor: activeCategory === cat.key ? `${cat.color}15` : 'white', color: activeCategory === cat.key ? cat.color : '#5E5F66', fontWeight: activeCategory === cat.key ? '700' : '500', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <cat.icon size={15} weight={activeCategory === cat.key ? 'fill' : 'regular'} />
            {cat.label} ({catCounts[cat.key] || 0})
          </button>
        ))}
      </div>

      {/* Search + Layout toggle */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un contact..." dataTestId="contact-search" style={{ width: '100%', maxWidth: 460 }} />
        <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="contacts-layout" />
      </div>

      {/* Contact list */}
      {filtered.length > 0 ? (
        (() => {
          const lp = getLayoutContainerProps(layoutMode, { cardMinWidth: '360px' });
          return (
            <div style={lp.style} className={lp.className || ''} data-testid={`contacts-container-${layoutMode}`}>
              {filtered.map(c => (
                <ContactCard key={c.id} contact={c} isAdmin={isAdmin} onEdit={handleEdit} onDelete={handleDelete} catColor={getCatColor(c.category)} />
              ))}
            </div>
          );
        })()
      ) : (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <AddressBook size={48} style={{ color: '#9E9E9E', opacity: 0.4, marginBottom: '12px' }} />
          <p style={{ color: '#5E5F66' }}>{searchTerm ? 'Aucun contact trouve' : 'Aucun contact dans cette categorie'}</p>
          {isAdmin && !searchTerm && (
            <button className="btn btn-primary" onClick={() => openAdd(activeCategory !== 'all' ? activeCategory : 'entreprise')} style={{ marginTop: '12px' }}>
              <Plus size={16} /> Ajouter un contact
            </button>
          )}
        </div>
      )}

      <ContactFormModal show={showModal} onClose={() => { setShowModal(false); setEditContact(null); }} onSubmit={handleSubmit} form={form} setForm={setForm} editMode={!!editContact} />
    </div>
  );
};

export default ContactsPage;
